import React from "react";
import { useTranslation } from "react-i18next";

import ViewContainer from "../../../components/ViewContainer";
import DashboardGrid from "../../../components/grid/DashboardGrid";
import Panel from "../../../components/grid/Panel";
import DataTable from "../../../components/DataTable";
import MultiSeriesLineChart from "../../../components/charts/MultiSeriesLineChart";
import SubgroupFilterBar, { useSubgroupFilter } from "./SubgroupFilterBar";

import qolData from "../../../data/pharma/subgroupsQol.json";

const brandHex = { primary: "#3248CA", primaryLight: "#5B6FD9", teal: "#ADCAD6", blueSlate: "#3D4F5F", accentMedium: "#FF8239", accent: "#FF5F24" };

// Modulo 3.2.2 — Subgroups · QoL trajectory.
function SubgroupsQolView() {
    const { t } = useTranslation();
    const { filtered } = useSubgroupFilter();

    const trajectoryData = qolData.trajectoryBySubgroup.months.map((m, i) => {
        const row = { x: m };
        filtered.forEach((s) => { row[s.id] = qolData.trajectoryBySubgroup.series[s.id][i]; });
        return row;
    });
    const trajectorySeries = filtered.map((s) => ({ key: s.id, name: s.name, color: brandHex[s.color] }));

    const metricsRows = filtered.map((s) => {
        const m = qolData.trajectoryMetrics.find((row) => row.subgroupId === s.id);
        return { subgroup: s.name, slope: m?.slopeLabel, plateau: m?.plateau, timeToPlateau: m?.timeToPlateau };
    });

    const rvo = qolData.reportedVsObserved;
    const rvoData = rvo.months.map((m, i) => ({ x: m, observed: rvo.observed[i], reported: rvo.reported[i] }));
    const rvoSeries = [
        { key: "observed", name: t("views.pharmaSubgroupsQol.series.observed"), color: brandHex.primary },
        { key: "reported", name: t("views.pharmaSubgroupsQol.series.reported"), color: brandHex.accent },
    ];

    return (
        <ViewContainer title={t("views.pharmaSubgroupsQol.title")} description={t("views.pharmaSubgroupsQol.description")}>
            <SubgroupFilterBar />
            <DashboardGrid gap={2} rowGap={2.5}>
                <Panel span={12} dense title={t("views.pharmaSubgroupsQol.trajectoryTitle")} subtitle={t("views.pharmaSubgroupsQol.trajectoryHint")}>
                    <MultiSeriesLineChart data={trajectoryData} series={trajectorySeries} yDomain={[0, 100]} height={280} />
                </Panel>

                <Panel span={{ xs: 12, md: 7 }} dense title={t("views.pharmaSubgroupsQol.metricsTitle")}>
                    <DataTable
                        columns={[
                            { key: "subgroup", label: t("views.pharmaSubgroupsQol.table.subgroup"), width: "1.2fr" },
                            { key: "slope", label: t("views.pharmaSubgroupsQol.table.slope"), align: "right", width: "1.3fr" },
                            { key: "plateau", label: t("views.pharmaSubgroupsQol.table.plateau"), align: "right", width: "0.8fr" },
                            { key: "timeToPlateau", label: t("views.pharmaSubgroupsQol.table.timeToPlateau"), align: "right", width: "0.8fr" },
                        ]}
                        rows={metricsRows}
                    />
                </Panel>

                <Panel span={{ xs: 12, md: 5 }} dense title={t("views.pharmaSubgroupsQol.reportedVsObservedTitle")} subtitle={t("views.pharmaSubgroupsQol.reportedVsObservedHint")}>
                    <MultiSeriesLineChart data={rvoData} series={rvoSeries} yDomain={[30, 60]} height={220} />
                </Panel>
            </DashboardGrid>
        </ViewContainer>
    );
}

export default SubgroupsQolView;
